/* eslint-disable max-len */
import React from "react";
import PropTypes from "prop-types";
import { format, isValid, addDays, addMonths } from "date-fns";
import { Formik, FieldArray } from "formik";
import * as Yup from "yup";
import Grid from "@mui/material/Grid";
import { InputAdornment } from "@mui/material";

import {
  Input,
  SelectAutocomplete,
  AutoSaveField as Field,
  NumberFormat,
  DatePicker
} from "../../../../../../components/Form";
import { Divider } from "../../../../../../components/Divider";
import { ArrayAddButton } from "../../../Buttons/ArrayAddButton";
import { ContinueButton } from "../../../../../../components/Buttons/ContinueButton";
import { DATE_FORMAT } from "../../../../../../constants";
import { DirhamSymbol } from "../../../../../../components/DirhamSymbol";
import { ANNUAL_TURNOVER_MAX_LENGTH } from "./constants";
import { DECIMAL_REGEX } from "../../../../../../utils/validation";
import { getInvalidMessage, getMinDateMessage } from "../../../../../../utils/getValidationMessage";

import {
  FormContainer,
  ButtonWrapper,
  InfoTextWrapper,
  AdornmentStyle,
  DeleteButton as ArrayRemoveButton,
  AddButton,
  RelativeBox
} from "./styled";
import { ScrollToError } from "components/ScrollToError";

const companyAnticipatedTransactionSchema = anticipatedTransactionsDetails =>
  Yup.object().shape({
    anticipatedTurnoverFromDate: Yup.date()
      .nullable()
      .min(new Date(1900, 0, 1), getMinDateMessage("From Anticipated Turnover"))
      .typeError(getInvalidMessage("From Anticipated Turnover")),
    anticipatedTurnoverToDate: Yup.date()
      .nullable()
      .min(new Date(1900, 0, 1), getMinDateMessage("To Anticipated Turnover"))
      .typeError(getInvalidMessage("To Anticipated Turnover")),
    anticipatedTurnover: Yup.string()
      .nullable()
      .max(13, "Maximum 13 characters allowed")
      .matches(DECIMAL_REGEX, getInvalidMessage("Anticipated turnover")),
    projectedTurnoverFromDate: Yup.date()
      .nullable()
      .min(new Date(lastYear, 0, 1), `Year should not be less than 01/01/${lastYear}`)
      .max(new Date(followingYear, 11, 31), getInvalidMessage("From Projected Turnover"))
      .typeError(getInvalidMessage("From Projected Turnover")),
    projectedTurnoverToDate: Yup.date()
      .nullable()
      .min(new Date(lastYear, 0, 1), `Year should not be less than 01/01/${lastYear}`)
      .max(new Date(followingYear, 11, 31), getInvalidMessage("To Projected Turnover"))
      .typeError(getInvalidMessage("To Projected Turnover")),
    projectedTurnoverFYE: Yup.string()
      .nullable()
      .max(13, "Maximum 13 characters allowed")
      .matches(DECIMAL_REGEX, getInvalidMessage("Projected Turnover for Next Year")),
    bankingTurnoverFromDate: Yup.date()
      .nullable()
      .min(new Date(1900, 0, 1), getMinDateMessage("From Banking Turnover"))
      .max(new Date(), getInvalidMessage("From Banking Turnover"))
      .typeError(getInvalidMessage("From Banking Turnover")),
    bankingTurnoverToDate: Yup.date()
      .nullable()
      .min(new Date(1900, 0, 1), getMinDateMessage("To Banking Turnover"))
      .typeError(getInvalidMessage("To Banking Turnover")),
    bankingTurnover: Yup.string()
      .nullable()
      .max(13, "Maximum 13 characters allowed")
      .matches(DECIMAL_REGEX, getInvalidMessage("Banking Turnover")),
    averageBalanceFromDate: Yup.date()
      .nullable()
      .min(new Date(1900, 0, 1), getMinDateMessage("From Average balance"))
      .max(new Date(), getInvalidMessage("From Average balance"))
      .typeError(getInvalidMessage("From Average balance")),
    averageBalanceToDate: Yup.date()
      .nullable()
      .min(new Date(1900, 0, 1), getMinDateMessage("To Average balance"))
      .typeError(getInvalidMessage("To Average balance")),
    averageBalance: Yup.string()
      .nullable()
      .max(13, "Maximum 13 characters allowed")
      .matches(DECIMAL_REGEX, getInvalidMessage("Average balance")),
    vatTurnoverFromDate: Yup.date()
      .nullable()
      .min(new Date(1900, 0, 1), getMinDateMessage("From VAT Turnover"))
      .max(new Date(), getInvalidMessage("From VAT Turnover"))
      .typeError(getInvalidMessage("From VAT Turnover")),
    vatTurnoverToDate: Yup.date()
      .nullable()
      .min(new Date(1900, 0, 1), getMinDateMessage("To VAT Turnover"))
      .typeError(getInvalidMessage("To VAT Turnover")),
    vatTurnover: Yup.string()
      .nullable()
      .max(13, "Maximum 13 characters allowed")
      .matches(DECIMAL_REGEX, getInvalidMessage("VAT turnover")),
    posTurnoverFromDate: Yup.date()
      .nullable()
      .min(new Date(1900, 0, 1), getMinDateMessage("From POS Turnover"))
      .max(new Date(), getInvalidMessage("From POS Turnover"))
      .typeError(getInvalidMessage("From POS Turnover")),
    posTurnoverToDate: Yup.date()
      .nullable()
      .min(new Date(1900, 0, 1), getMinDateMessage("To POS Turnover"))
      .typeError(getInvalidMessage("To POS Turnover")),
    posTurnover: Yup.string()
      .nullable()
      .max(13, "Maximum 13 characters allowed")
      .matches(DECIMAL_REGEX, getInvalidMessage("POS Turnover")),
    ytdSales: Yup.array().of(
      Yup.object().shape({
        year: Yup.date()
          .nullable()
          .min(new Date(lastYear, 0, 1), `Year should not be less than 01/01/${lastYear}`)
          .max(new Date(followingYear, 11, 31), getInvalidMessage("YTD Sales Year"))
          .typeError(getInvalidMessage("YTD Sales Year")),
        amount: Yup.string()
          .nullable()
          .max(13, "Maximum 13 characters allowed")
          .matches(DECIMAL_REGEX, getInvalidMessage("YTD Sales Amount"))
      })
    ),
    grossProfit: Yup.array().of(
      Yup.object().shape({
        year: Yup.date()
          .nullable()
          .min(new Date(lastYear, 0, 1), `Year should not be less than 01/01/${lastYear}`)
          .max(new Date(followingYear, 11, 31), getInvalidMessage("Gross Profit Year"))
          .typeError(getInvalidMessage("Gross Profit Year"))
          .test("Same date check", "Same year should not be added again", function(value) {
            const { grossProfit = [] } = anticipatedTransactionsDetails;
            if (value) {
              let count = 0;
              grossProfit.forEach(val => {
                if (new Date(val.year).getFullYear() === new Date(value).getFullYear()) {
                  count += 1;
                }
              });
              return count <= 1;
            }
            return true;
          }),
        profit: Yup.string().nullable()
      })
    ),
    netProfit: Yup.array().of(
      Yup.object().shape({
        year: Yup.date()
          .nullable()
          .min(new Date(lastYear, 0, 1), `Year should not be less than 01/01/${lastYear}`)
          .max(new Date(followingYear, 11, 31), getInvalidMessage("Net Profit Year"))
          .typeError(getInvalidMessage("Net Profit Year")),
        profit: Yup.string().nullable()
      })
    ),
    shareCapital: Yup.array().of(
      Yup.object().shape({
        year: Yup.date()
          .nullable()
          .min(new Date(lastYear, 0, 1), `Year should not be less than 01/01/${lastYear}`)
          .max(new Date(followingYear, 11, 31), getInvalidMessage("Share capital Year"))
          .typeError(getInvalidMessage("Share capital Year")),
        amount: Yup.string()
          .max(13, "Maximum 13 characters allowed")
          .matches(DECIMAL_REGEX, getInvalidMessage("Share Capital"))
      })
    ),
    networth: Yup.array().of(
      Yup.object().shape({
        year: Yup.date()
          .nullable()
          .min(new Date(lastYear, 0, 1), `Year should not be less than 01/01/${lastYear}`)
          .max(new Date(followingYear, 11, 31), getInvalidMessage("Net worth Year"))
          .typeError(getInvalidMessage("Net worth Year")),
        amount: Yup.string()
          .max(13, "Maximum 13 characters allowed")
          .matches(DECIMAL_REGEX, getInvalidMessage("Net Worth"))
      })
    )
  });

const initialanticipatedTransactions = {
  anticipatedTurnoverfrequency: "",
  anticipatedTurnoverFromDate: "",
  anticipatedTurnoverToDate: "",
  anticipatedTurnover: "",
  projectedTurnoverFromDate: "",
  projectedTurnoverToDate: "",
  projectedTurnoverFYE: "",
  bankingTurnoverfrequency: "",
  bankingTurnoverFromDate: "",
  bankingTurnoverToDate: "",
  bankingTurnover: "",
  averageBalancefrequency: "",
  averageBalanceFromDate: "",
  averageBalanceToDate: "",
  averageBalance: "",
  vatTurnoverfrequency: "",
  vatTurnoverFromDate: "",
  vatTurnoverToDate: "",
  vatTurnover: "",
  posTurnoverfrequency: "",
  posTurnoverFromDate: "",
  posTurnoverToDate: "",
  posTurnover: "",
  noOfCreditsPerQuarter: "",
  noOfCreditsPerMonth: ""
};
const initialestimatedSales = ytdSales => {
  return ytdSales
    ? ytdSales.map(profit => profit)
    : [
        {
          year: "",
          amount: ""
        }
      ];
};

const ymdToDateObject = ymdDate => {
  const dateArr = ymdDate.split("-");
  return new Date(dateArr[0], dateArr[1] - 1, dateArr[2]);
};

const initialestimatedGrossProfit = grossProfit => {
  return grossProfit
    ? grossProfit.map(profit => profit)
    : [
        {
          year: "",
          profit: ""
        }
      ];
};

const initialestimatedNetProfit = netProfit => {
  return netProfit
    ? netProfit.map(profit => profit)
    : [
        {
          year: "",
          profit: ""
        }
      ];
};

const initialestimatedShareCapital = shareCapital => {
  return shareCapital
    ? shareCapital.map(worth => worth)
    : [
        {
          year: "",
          amount: ""
        }
      ];
};

const initialestimatedNetWorth = networth => {
  return networth
    ? networth.map(worth => worth)
    : [
        {
          year: "",
          amount: ""
        }
      ];
};

const initialValues = anticipatedTransactionsDetails => {
  const {
    networth,
    grossProfit,
    netProfit,
    shareCapital,
    ytdSales
  } = anticipatedTransactionsDetails;
  return {
    ...initialanticipatedTransactions,
    ytdSales: initialestimatedSales(ytdSales),
    grossProfit: initialestimatedGrossProfit(grossProfit),
    netProfit: initialestimatedNetProfit(netProfit),
    shareCapital: initialestimatedShareCapital(shareCapital),
    networth: initialestimatedNetWorth(networth)
  };
};
const FormatDecimalNumberInput = props => {
  const MAX_VAL = 999;
  const MIN_VAL = -999;
  const withValueLimit = ({ floatValue }) =>
    floatValue ? (floatValue > 0 ? floatValue <= MAX_VAL : floatValue >= MIN_VAL) : true;
  return (
    <NumberFormat
      allowNegative={true}
      thousandSeparator={true}
      decimalScale={2}
      isAllowed={withValueLimit}
      {...props}
    />
  );
};

const nextYear = new Date();
nextYear.setFullYear(nextYear.getFullYear() + 1, 11, 1);
const followingYear = nextYear.getFullYear();
const prevYear = new Date();
prevYear.setFullYear(prevYear.getFullYear() - 1, 0, 1);
const lastYear = prevYear.getFullYear();

const changeDateProspectHandler = (_, value, path) =>
  isValid(value) && { [path]: format(value, DATE_FORMAT) };

export const CompanyAnticipatedTransactionComponent = ({
  anticipatedTransactionsDetails,
  handleContinue,
  createFormChangeHandler,
  updateRoProspect
}) => {
  const commonInputProps = {
    endAdornment: (
      <InputAdornment position="end">
        <DirhamSymbol />
      </InputAdornment>
    ),
    inputProps: { tabIndex: 0 }
  };
  const commonPercentageInputProps = {
    endAdornment: (
      <InputAdornment position="end">
        <AdornmentStyle>%</AdornmentStyle>
      </InputAdornment>
    ),
    inputProps: { tabIndex: 0 },
    inputComponent: FormatDecimalNumberInput
  };

  return (
    <Formik
      initialValues={initialValues(anticipatedTransactionsDetails)}
      validationSchema={companyAnticipatedTransactionSchema(anticipatedTransactionsDetails)}
      onSubmit={handleContinue}
      validateOnChange={false}
    >
      {createFormChangeHandler(({ values, setFieldValue }) => {
        const estimatedSales = values.ytdSales.length > 1;
        const estimatedGrossProfit = values.grossProfit.length > 1;
        const estimatedNetProfit = values.netProfit.length > 1;
        const estimatedShareCapital = values.shareCapital.length > 1;
        const estimatedNetWorth = values.networth.length > 1;
        return (
          <FormContainer>
            <ScrollToError />
            <Grid container spacing={3}>
              <Grid item sm={12} xs={12}>
                <h5> Projected Finances </h5>
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="projectedTurnoverFromDate"
                  path="roProspect.organizationInfoRO.anticipatedTransactionsDetails.projectedTurnoverFromDate"
                  label="From Projected Turnover"
                  component={DatePicker}
                  openTo="year"
                  views={["year"]}
                  placeholder="____"
                  minDate={prevYear}
                  maxDate={nextYear}
                  format="yyyy"
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                  changeProspect={changeDateProspectHandler}
                  disableFuture={false}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="projectedTurnoverToDate"
                  path="roProspect.organizationInfoRO.anticipatedTransactionsDetails.projectedTurnoverToDate"
                  label="To Projected Turnover"
                  component={DatePicker}
                  openTo="year"
                  views={["year"]}
                  placeholder="____"
                  minDate={prevYear}
                  maxDate={nextYear}
                  format="yyyy"
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                  changeProspect={changeDateProspectHandler}
                  disableFuture={false}
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  name="projectedTurnoverFYE"
                  path="roProspect.organizationInfoRO.anticipatedTransactionsDetails.projectedTurnoverFYE"
                  label="Projected Turnover for Next Year"
                  autoComplete="off"
                  placeholder="Projected Turnover for Next Year"
                  InputProps={{
                    ...commonInputProps,
                    inputProps: {
                      maxLength: ANNUAL_TURNOVER_MAX_LENGTH,
                      tabIndex: 0
                    }
                  }}
                  component={Input}
                />
              </Grid>
            </Grid>
            <Divider.Dark />
            <Grid container spacing={3}>
              <Grid item sm={12} xs={12}>
                <h5> Banking Turnover </h5>
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="bankingTurnoverfrequency"
                  path="roProspect.organizationInfoRO.anticipatedTransactionsDetails.bankingTurnoverfrequency"
                  label="Frequency"
                  component={SelectAutocomplete}
                  datalistId="turnoverDuration"
                  onChange={value => {
                    let fromDate;
                    let toDate;
                    if (anticipatedTransactionsDetails?.bankingTurnoverFromDate.length === 10) {
                      fromDate = ymdToDateObject(
                        anticipatedTransactionsDetails.bankingTurnoverFromDate
                      );
                      toDate = addMonths(fromDate, parseInt(value));
                    } else {
                      toDate = addDays(new Date(), -1);
                      fromDate = addMonths(toDate, parseInt(value) * -1);
                    }
                    setFieldValue("bankingTurnoverfrequency", value);
                    setFieldValue("bankingTurnoverFromDate", fromDate);
                    setFieldValue("bankingTurnoverToDate", toDate);
                  }}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="bankingTurnoverFromDate"
                  path="roProspect.organizationInfoRO.anticipatedTransactionsDetails.bankingTurnoverFromDate"
                  label="From Banking Turnover"
                  component={DatePicker}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                  changeProspect={changeDateProspectHandler}
                  onChange={date => {
                    if (isValid(date) && values.bankingTurnoverfrequency) {
                      const fromDate = date;
                      const toDate = addMonths(fromDate, parseInt(values.bankingTurnoverfrequency));
                      setFieldValue("bankingTurnoverFromDate", fromDate);
                      setFieldValue("bankingTurnoverToDate", toDate);
                    }
                  }}
                  disabled={!values.bankingTurnoverfrequency}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="bankingTurnoverToDate"
                  path="roProspect.organizationInfoRO.anticipatedTransactionsDetails.bankingTurnoverToDate"
                  label="To Banking Turnover"
                  component={DatePicker}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                  changeProspect={changeDateProspectHandler}
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  name="bankingTurnover"
                  path="roProspect.organizationInfoRO.anticipatedTransactionsDetails.bankingTurnover"
                  label="Banking Turnover"
                  autoComplete="off"
                  placeholder="Banking Turnover"
                  InputProps={{
                    ...commonInputProps,
                    inputProps: {
                      maxLength: ANNUAL_TURNOVER_MAX_LENGTH,
                      tabIndex: 0
                    }
                  }}
                  component={Input}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="averageBalancefrequency"
                  path="roProspect.organizationInfoRO.anticipatedTransactionsDetails.averageBalancefrequency"
                  label="Frequency"
                  component={SelectAutocomplete}
                  datalistId="turnoverDuration"
                  onChange={value => {
                    let fromDate;
                    let toDate;
                    if (anticipatedTransactionsDetails?.averageBalanceFromDate.length === 10) {
                      fromDate = ymdToDateObject(
                        anticipatedTransactionsDetails.averageBalanceFromDate
                      );
                      toDate = addMonths(fromDate, parseInt(value));
                    } else {
                      toDate = addDays(new Date(), -1);
                      fromDate = addMonths(toDate, parseInt(value) * -1);
                    }
                    setFieldValue("averageBalancefrequency", value);
                    setFieldValue("averageBalanceFromDate", fromDate);
                    setFieldValue("averageBalanceToDate", toDate);
                  }}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="averageBalanceFromDate"
                  path="roProspect.organizationInfoRO.anticipatedTransactionsDetails.averageBalanceFromDate"
                  label="From Average balance"
                  component={DatePicker}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                  changeProspect={changeDateProspectHandler}
                  onChange={date => {
                    if (isValid(date) && values.averageBalancefrequency) {
                      const fromDate = date;
                      const toDate = addMonths(fromDate, parseInt(values.averageBalancefrequency));
                      setFieldValue("averageBalanceFromDate", fromDate);
                      setFieldValue("averageBalanceToDate", toDate);
                    }
                  }}
                  disabled={!values.averageBalancefrequency}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="averageBalanceToDate"
                  path="roProspect.organizationInfoRO.anticipatedTransactionsDetails.averageBalanceToDate"
                  label="To Average balance"
                  component={DatePicker}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                  changeProspect={changeDateProspectHandler}
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  name="averageBalance"
                  label="Average balance"
                  path="roProspect.organizationInfoRO.anticipatedTransactionsDetails.averageBalance"
                  placeholder="Average balance"
                  InputProps={{
                    ...commonInputProps,
                    inputProps: {
                      maxLength: ANNUAL_TURNOVER_MAX_LENGTH,
                      tabIndex: 0
                    }
                  }}
                  component={Input}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="vatTurnoverfrequency"
                  path="roProspect.organizationInfoRO.anticipatedTransactionsDetails.vatTurnoverfrequency"
                  label="Frequency"
                  component={SelectAutocomplete}
                  datalistId="turnoverDuration"
                  onChange={value => {
                    let fromDate;
                    let toDate;
                    if (anticipatedTransactionsDetails?.vatTurnoverFromDate.length === 10) {
                      fromDate = ymdToDateObject(
                        anticipatedTransactionsDetails.vatTurnoverFromDate
                      );
                      toDate = addMonths(fromDate, parseInt(value));
                    } else {
                      toDate = addDays(new Date(), -1);
                      fromDate = addMonths(toDate, parseInt(value) * -1);
                    }
                    setFieldValue("vatTurnoverfrequency", value);
                    setFieldValue("vatTurnoverFromDate", fromDate);
                    setFieldValue("vatTurnoverToDate", toDate);
                  }}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="vatTurnoverFromDate"
                  path="roProspect.organizationInfoRO.anticipatedTransactionsDetails.vatTurnoverFromDate"
                  label="From VAT Turnover"
                  component={DatePicker}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                  changeProspect={changeDateProspectHandler}
                  onChange={date => {
                    if (isValid(date) && values.vatTurnoverfrequency) {
                      const fromDate = date;
                      const toDate = addMonths(fromDate, parseInt(values.vatTurnoverfrequency));
                      setFieldValue("vatTurnoverFromDate", fromDate);
                      setFieldValue("vatTurnoverToDate", toDate);
                    }
                  }}
                  disabled={!values.vatTurnoverfrequency}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="vatTurnoverToDate"
                  path="roProspect.organizationInfoRO.anticipatedTransactionsDetails.vatTurnoverToDate"
                  label="To VAT Turnover"
                  component={DatePicker}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                  changeProspect={changeDateProspectHandler}
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  name="vatTurnover"
                  path="roProspect.organizationInfoRO.anticipatedTransactionsDetails.vatTurnover"
                  label="VAT turnover"
                  autoComplete="off"
                  placeholder="VAT turnover"
                  InputProps={{
                    ...commonInputProps,
                    inputProps: {
                      maxLength: ANNUAL_TURNOVER_MAX_LENGTH,
                      tabIndex: 0
                    }
                  }}
                  component={Input}
                />
              </Grid>
              <Divider.Dark />
              <Grid item sm={6} xs={12}>
                <Field
                  name="posTurnoverfrequency"
                  path="roProspect.organizationInfoRO.anticipatedTransactionsDetails.posTurnoverfrequency"
                  label="Frequency"
                  component={SelectAutocomplete}
                  datalistId="turnoverDuration"
                  onChange={value => {
                    let fromDate;
                    let toDate;
                    if (anticipatedTransactionsDetails?.posTurnoverFromDate.length === 10) {
                      fromDate = ymdToDateObject(
                        anticipatedTransactionsDetails.posTurnoverFromDate
                      );
                      toDate = addMonths(fromDate, parseInt(value));
                    } else {
                      toDate = addDays(new Date(), -1);
                      fromDate = addMonths(toDate, parseInt(value) * -1);
                    }
                    setFieldValue("posTurnoverfrequency", value);
                    setFieldValue("posTurnoverFromDate", fromDate);
                    setFieldValue("posTurnoverToDate", toDate);
                  }}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="posTurnoverFromDate"
                  path="roProspect.organizationInfoRO.anticipatedTransactionsDetails.posTurnoverFromDate"
                  label="From POS Turnover"
                  component={DatePicker}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                  changeProspect={changeDateProspectHandler}
                  onChange={date => {
                    if (isValid(date) && values.posTurnoverfrequency) {
                      const fromDate = date;
                      const toDate = addMonths(fromDate, parseInt(values.posTurnoverfrequency));
                      setFieldValue("posTurnoverFromDate", fromDate);
                      setFieldValue("posTurnoverToDate", toDate);
                    }
                  }}
                  disabled={!values.posTurnoverfrequency}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="posTurnoverToDate"
                  path="roProspect.organizationInfoRO.anticipatedTransactionsDetails.posTurnoverToDate"
                  label="To POS Turnover"
                  component={DatePicker}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                  changeProspect={changeDateProspectHandler}
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  name="posTurnover"
                  path="roProspect.organizationInfoRO.anticipatedTransactionsDetails.posTurnover"
                  label="POS Turnover"
                  autoComplete="off"
                  placeholder="POS Turnover"
                  InputProps={{
                    ...commonInputProps,
                    inputProps: {
                      maxLength: ANNUAL_TURNOVER_MAX_LENGTH,
                      tabIndex: 0
                    }
                  }}
                  component={Input}
                />
              </Grid>
            </Grid>
            <Divider.Dark />
            <Grid container spacing={3}>
              <Grid item sm={12} xs={12}>
                <h5> Anticipated Transactions </h5>
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="anticipatedTurnoverfrequency"
                  path="roProspect.organizationInfoRO.anticipatedTransactionsDetails.anticipatedTurnoverfrequency"
                  label="Frequency"
                  component={SelectAutocomplete}
                  datalistId="turnoverDuration"
                  onChange={value => {
                    let fromDate;
                    let toDate;
                    if (anticipatedTransactionsDetails?.anticipatedTurnoverFromDate.length === 10) {
                      fromDate = ymdToDateObject(
                        anticipatedTransactionsDetails.anticipatedTurnoverFromDate
                      );
                      toDate = addMonths(fromDate, parseInt(value));
                    } else {
                      toDate = addDays(new Date(), -1);
                      fromDate = addMonths(toDate, parseInt(value) * -1);
                    }

                    setFieldValue("anticipatedTurnoverfrequency", value);
                    setFieldValue("anticipatedTurnoverFromDate", fromDate);
                    setFieldValue("anticipatedTurnoverToDate", toDate);
                  }}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="anticipatedTurnoverFromDate"
                  path="roProspect.organizationInfoRO.anticipatedTransactionsDetails.anticipatedTurnoverFromDate"
                  label="From Anticipated Turnover"
                  component={DatePicker}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                  changeProspect={changeDateProspectHandler}
                  onChange={date => {
                    if (isValid(date) && values.anticipatedTurnoverFromDate) {
                      const fromDate = date;
                      const toDate = addMonths(
                        fromDate,
                        parseInt(values.anticipatedTurnoverfrequency)
                      );
                      setFieldValue("anticipatedTurnoverFromDate", fromDate);
                      setFieldValue("anticipatedTurnoverToDate", toDate);
                    }
                  }}
                  disabled={!values.anticipatedTurnoverfrequency}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="anticipatedTurnoverToDate"
                  path="roProspect.organizationInfoRO.anticipatedTransactionsDetails.anticipatedTurnoverToDate"
                  label="To Anticipated Turnover"
                  component={DatePicker}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                  changeProspect={changeDateProspectHandler}
                  disabled
                />
              </Grid>
              <Grid item sm={9} xs={12}>
                <Field
                  name="anticipatedTurnover"
                  path="roProspect.organizationInfoRO.anticipatedTransactionsDetails.anticipatedTurnover"
                  label="Anticipated turnover (current year)"
                  autoComplete="off"
                  placeholder="Anticipated turnover (current year)"
                  InputProps={{
                    ...commonInputProps,
                    inputProps: {
                      maxLength: ANNUAL_TURNOVER_MAX_LENGTH,
                      tabIndex: 0
                    }
                  }}
                  component={Input}
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  name="noOfCreditsPerQuarter"
                  path="roProspect.organizationInfoRO.anticipatedTransactionsDetails.noOfCreditsPerQuarter"
                  label="No of credits per quarter (Count)"
                  component={SelectAutocomplete}
                  datalistId="noOfCreditsQuarter"
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="noOfCreditsPerMonth"
                  path="roProspect.organizationInfoRO.anticipatedTransactionsDetails.noOfCreditsPerMonth"
                  label="No of credits per month (Count)"
                  component={SelectAutocomplete}
                  datalistId="noOfMonthCredit"
                />
              </Grid>
            </Grid>
            <Divider.Dark />
            <Grid container spacing={3}>
              <Grid item sm={12} xs={12}>
                <h5> Estimated Financial Details </h5>
              </Grid>
              <Grid item sm={4} xs={12}>
                <h5> Gross Profit </h5>
                <InfoTextWrapper>
                  Mandatory to include last, current and projected year
                </InfoTextWrapper>
              </Grid>
              <Grid item sm={8} xs={12} mt={2}>
                <FieldArray name="grossProfit">
                  {estimatedGrossProfitArrayHelpers => (
                    <>
                      <Grid container spacing={3}>
                        {values.grossProfit.map((item, index) => (
                          <React.Fragment key={index}>
                            <Grid item sm={5} xs={12}>
                              <Field
                                name={`grossProfit[${index}].year`}
                                path={`roProspect.organizationInfoRO.anticipatedTransactionsDetails.grossProfit[${index}].year`}
                                label="Year"
                                component={DatePicker}
                                openTo="year"
                                views={["year"]}
                                placeholder="____"
                                minDate={prevYear}
                                maxDate={nextYear}
                                format="yyyy"
                                changeProspect={changeDateProspectHandler}
                                InputProps={{
                                  inputProps: { tabIndex: 0 }
                                }}
                                disableFuture={false}
                              />
                            </Grid>
                            <Grid
                              item
                              sm={estimatedGrossProfit ? 5 : 7}
                              xs={12}
                              component={RelativeBox}
                            >
                              <Field
                                name={`grossProfit[${index}].profit`}
                                path={`roProspect.organizationInfoRO.anticipatedTransactionsDetails.grossProfit[${index}].profit`}
                                component={Input}
                                InputProps={{
                                  ...commonPercentageInputProps,
                                  inputProps: { tabIndex: 0 }
                                }}
                              />
                            </Grid>
                            {estimatedGrossProfit && (
                              <Grid item sm={2} xs={12} alignSelf={"center"}>
                                <ArrayRemoveButton
                                  arrayHelpers={estimatedGrossProfitArrayHelpers}
                                  dataArray={anticipatedTransactionsDetails.grossProfit}
                                  itemIndex={index}
                                  prospectPath="roProspect.organizationInfoRO.anticipatedTransactionsDetails.grossProfit"
                                  updateProspect={updateRoProspect}
                                  title="Delete"
                                />
                              </Grid>
                            )}
                          </React.Fragment>
                        ))}
                      </Grid>
                      <ArrayAddButton
                        title="Add"
                        limit={3}
                        requiredFields={["estimatedGrossProfitAmount"]}
                        addedItem={{
                          year: "",
                          profit: ""
                        }}
                        dataArray={values.grossProfit}
                        arrayHelpers={estimatedGrossProfitArrayHelpers}
                        className={AddButton}
                        sx={{ mt: 2 }}
                      />
                    </>
                  )}
                </FieldArray>
              </Grid>
              <Divider.Dark />
              <Grid item sm={4} xs={12}>
                <h5> Net Profit </h5>
                <InfoTextWrapper>
                  Mandatory to include last, current and projected year
                </InfoTextWrapper>
              </Grid>
              <Grid item sm={8} xs={12} mt={2}>
                <FieldArray name="netProfit">
                  {estimatedNetProfitArrayHelpers => (
                    <>
                      <Grid container spacing={3}>
                        {values.netProfit.map((item, index) => (
                          <React.Fragment key={index}>
                            <Grid item sm={5} xs={6}>
                              <Field
                                name={`netProfit[${index}].year`}
                                path={`roProspect.organizationInfoRO.anticipatedTransactionsDetails.netProfit[${index}].year`}
                                label="Year"
                                component={DatePicker}
                                openTo="year"
                                views={["year"]}
                                placeholder="____"
                                minDate={prevYear}
                                maxDate={nextYear}
                                format="yyyy"
                                changeProspect={changeDateProspectHandler}
                                InputProps={{
                                  inputProps: { tabIndex: 0 }
                                }}
                                disableFuture={false}
                              />
                            </Grid>
                            <Grid
                              item
                              sm={estimatedNetProfit ? 5 : 7}
                              xs={12}
                              component={RelativeBox}
                            >
                              <Field
                                name={`netProfit[${index}].profit`}
                                path={`roProspect.organizationInfoRO.anticipatedTransactionsDetails.netProfit[${index}].profit`}
                                component={Input}
                                InputProps={{
                                  ...commonPercentageInputProps,
                                  inputProps: {
                                    tabIndex: 0
                                  }
                                }}
                              />
                            </Grid>
                            {estimatedNetProfit && (
                              <Grid item sm={2} xs={12} alignSelf={"center"}>
                                <ArrayRemoveButton
                                  arrayHelpers={estimatedNetProfitArrayHelpers}
                                  dataArray={anticipatedTransactionsDetails.netProfit}
                                  itemIndex={index}
                                  prospectPath="roProspect.organizationInfoRO.anticipatedTransactionsDetails.netProfit"
                                  updateProspect={updateRoProspect}
                                  title="Delete"
                                />
                              </Grid>
                            )}
                          </React.Fragment>
                        ))}
                      </Grid>
                      <ArrayAddButton
                        title="Add"
                        limit={3}
                        requiredFields={["estimatedNetProfitPercent"]}
                        addedItem={{
                          year: "",
                          profit: ""
                        }}
                        dataArray={values.netProfit}
                        arrayHelpers={estimatedNetProfitArrayHelpers}
                        className={AddButton}
                        sx={{ mt: 2 }}
                      />
                    </>
                  )}
                </FieldArray>
              </Grid>
              <Divider.Dark />
              <Grid item sm={4} xs={12}>
                <h5> YTD Sales </h5>
                <InfoTextWrapper>
                  Mandatory to include last, current and projected year
                </InfoTextWrapper>
              </Grid>
              <Grid item sm={8} xs={12}>
                <FieldArray name="ytdSales">
                  {estimatedSalesArrayHelpers => (
                    <>
                      <Grid container spacing={3}>
                        {values.ytdSales.map((item, index) => (
                          <React.Fragment key={index}>
                            <Grid item sm={5} xs={12}>
                              <Field
                                name={`ytdSales[${index}].year`}
                                path={`roProspect.organizationInfoRO.anticipatedTransactionsDetails.ytdSales[${index}].year`}
                                label="Year"
                                component={DatePicker}
                                openTo="year"
                                views={["year"]}
                                placeholder="____"
                                minDate={prevYear}
                                maxDate={nextYear}
                                format="yyyy"
                                changeProspect={changeDateProspectHandler}
                                InputProps={{
                                  inputProps: { tabIndex: 0 }
                                }}
                                disableFuture={false}
                              />
                            </Grid>
                            <Grid item sm={estimatedSales ? 5 : 7} xs={12} component={RelativeBox}>
                              <Field
                                name={`ytdSales[${index}].amount`}
                                path={`roProspect.organizationInfoRO.anticipatedTransactionsDetails.ytdSales[${index}].amount`}
                                component={Input}
                                InputProps={{
                                  ...commonInputProps,
                                  inputProps: {
                                    maxLength: ANNUAL_TURNOVER_MAX_LENGTH,
                                    tabIndex: 0
                                  }
                                }}
                              />
                            </Grid>
                            {estimatedSales && (
                              <Grid item sm={2} xs={12} alignSelf={"center"}>
                                <ArrayRemoveButton
                                  arrayHelpers={estimatedSalesArrayHelpers}
                                  dataArray={anticipatedTransactionsDetails.ytdSales}
                                  itemIndex={index}
                                  prospectPath="roProspect.organizationInfoRO.anticipatedTransactionsDetails.ytdSales"
                                  updateProspect={updateRoProspect}
                                  title="Delete"
                                />
                              </Grid>
                            )}
                          </React.Fragment>
                        ))}
                      </Grid>
                      <ArrayAddButton
                        title="Add"
                        limit={3}
                        requiredFields={["estimatedSalesAmount"]}
                        addedItem={{
                          year: "",
                          amount: ""
                        }}
                        dataArray={values.ytdSales}
                        arrayHelpers={estimatedSalesArrayHelpers}
                        className={AddButton}
                      />
                    </>
                  )}
                </FieldArray>
              </Grid>
              <Divider.Dark />
              <Grid item sm={4} xs={12}>
                <h5> Share Capital </h5>
                <InfoTextWrapper>
                  Mandatory to include last, current and projected year
                </InfoTextWrapper>
              </Grid>
              <Grid item sm={8} xs={12}>
                <FieldArray name="shareCapital">
                  {estimatedShareCapitalArrayHelpers => (
                    <>
                      <Grid container spacing={3}>
                        {values.shareCapital.map((item, index) => (
                          <React.Fragment key={index}>
                            <Grid item sm={5} xs={12}>
                              <Field
                                name={`shareCapital[${index}].year`}
                                path={`roProspect.organizationInfoRO.anticipatedTransactionsDetails.shareCapital[${index}].year`}
                                label="Year"
                                component={DatePicker}
                                openTo="year"
                                views={["year"]}
                                placeholder="____"
                                minDate={prevYear}
                                maxDate={nextYear}
                                format="yyyy"
                                changeProspect={changeDateProspectHandler}
                                InputProps={{
                                  inputProps: { tabIndex: 0 }
                                }}
                                disableFuture={false}
                              />
                            </Grid>
                            <Grid
                              item
                              sm={estimatedShareCapital ? 5 : 7}
                              xs={12}
                              component={RelativeBox}
                            >
                              <Field
                                name={`shareCapital[${index}].amount`}
                                path={`roProspect.organizationInfoRO.anticipatedTransactionsDetails.shareCapital[${index}].amount`}
                                component={Input}
                                InputProps={{
                                  ...commonInputProps,
                                  inputProps: {
                                    maxLength: ANNUAL_TURNOVER_MAX_LENGTH,
                                    tabIndex: 0
                                  }
                                }}
                              />
                            </Grid>
                            {estimatedShareCapital && (
                              <Grid item sm={2} xs={12} alignSelf={"center"}>
                                <ArrayRemoveButton
                                  arrayHelpers={estimatedShareCapitalArrayHelpers}
                                  dataArray={anticipatedTransactionsDetails.shareCapital}
                                  itemIndex={index}
                                  prospectPath="roProspect.organizationInfoRO.anticipatedTransactionsDetails.shareCapital"
                                  updateProspect={updateRoProspect}
                                  title="Delete"
                                />
                              </Grid>
                            )}
                          </React.Fragment>
                        ))}
                      </Grid>
                      <ArrayAddButton
                        title="Add"
                        limit={3}
                        requiredFields={["estimatedShareCapitalAmount"]}
                        addedItem={{
                          year: "",
                          amount: ""
                        }}
                        dataArray={values.shareCapital}
                        arrayHelpers={estimatedShareCapitalArrayHelpers}
                        className={AddButton}
                      />
                    </>
                  )}
                </FieldArray>
              </Grid>
              <Divider.Dark />
              <Grid item sm={4} xs={12}>
                <h5> Net Worth </h5>
                <InfoTextWrapper>
                  Mandatory to include last, current and projected year
                </InfoTextWrapper>
              </Grid>
              <Grid item sm={8} xs={12}>
                <FieldArray name="networth">
                  {estimatedNetWorthArrayHelpers => (
                    <>
                      <Grid container spacing={3}>
                        {values.networth.map((item, index) => {
                          return (
                            <React.Fragment key={index}>
                              <Grid item sm={5} xs={12}>
                                <Field
                                  name={`networth[${index}].year`}
                                  path={`roProspect.organizationInfoRO.anticipatedTransactionsDetails.networth[${index}].year`}
                                  label="Year"
                                  component={DatePicker}
                                  openTo="year"
                                  views={["year"]}
                                  placeholder="____"
                                  minDate={prevYear}
                                  maxDate={nextYear}
                                  format="yyyy"
                                  changeProspect={changeDateProspectHandler}
                                  InputProps={{
                                    inputProps: { tabIndex: 0 }
                                  }}
                                  disableFuture={false}
                                />
                              </Grid>
                              <Grid
                                item
                                sm={estimatedNetWorth ? 5 : 7}
                                xs={12}
                                component={RelativeBox}
                              >
                                <Field
                                  name={`networth[${index}].amount`}
                                  path={`roProspect.organizationInfoRO.anticipatedTransactionsDetails.networth[${index}].amount`}
                                  component={Input}
                                  InputProps={{
                                    ...commonInputProps,
                                    inputProps: {
                                      maxLength: ANNUAL_TURNOVER_MAX_LENGTH,
                                      tabIndex: 0
                                    }
                                  }}
                                />
                              </Grid>
                              {estimatedNetWorth && (
                                <Grid item sm={2} xs={12} alignSelf={"center"}>
                                  <ArrayRemoveButton
                                    arrayHelpers={estimatedNetWorthArrayHelpers}
                                    dataArray={anticipatedTransactionsDetails.networth}
                                    itemIndex={index}
                                    prospectPath="roProspect.organizationInfoRO.anticipatedTransactionsDetails.networth"
                                    updateProspect={updateRoProspect}
                                    title="Delete"
                                  />
                                </Grid>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </Grid>
                      <ArrayAddButton
                        title="Add"
                        limit={3}
                        requiredFields={["estimatedNetWorthAmount"]}
                        addedItem={{
                          year: "",
                          amount: ""
                        }}
                        dataArray={values.networth}
                        arrayHelpers={estimatedNetWorthArrayHelpers}
                        className={AddButton}
                      />
                    </>
                  )}
                </FieldArray>
              </Grid>
              <Divider.Dark />
            </Grid>
            <Divider.Dark />
            <Grid>
              <ButtonWrapper>
                <ContinueButton type="submit" />
              </ButtonWrapper>
            </Grid>
          </FormContainer>
        );
      })}
    </Formik>
  );
};

CompanyAnticipatedTransactionComponent.propTypes = {
  /**
   * Object containing anticipated transactions details including ytdSales, grossProfit, netProfit, shareCapital, and networth
   */
  anticipatedTransactionsDetails: PropTypes.shape({
    anticipatedTurnoverfrequency: PropTypes.string,
    anticipatedTurnoverFromDate: PropTypes.string,
    anticipatedTurnoverToDate: PropTypes.string,
    anticipatedTurnover: PropTypes.string,
    projectedTurnoverFromDate: PropTypes.string,
    projectedTurnoverToDate: PropTypes.string,
    projectedTurnoverFYE: PropTypes.string,
    bankingTurnoverfrequency: PropTypes.string,
    bankingTurnoverFromDate: PropTypes.string,
    bankingTurnoverToDate: PropTypes.string,
    bankingTurnover: PropTypes.string,
    averageBalancefrequency: PropTypes.string,
    averageBalanceFromDate: PropTypes.string,
    averageBalanceToDate: PropTypes.string,
    averageBalance: PropTypes.string,
    vatTurnoverfrequency: PropTypes.string,
    vatTurnoverFromDate: PropTypes.string,
    vatTurnoverToDate: PropTypes.string,
    vatTurnover: PropTypes.string,
    posTurnoverfrequency: PropTypes.string,
    posTurnoverFromDate: PropTypes.string,
    posTurnoverToDate: PropTypes.string,
    posTurnover: PropTypes.string,
    noOfCreditsPerQuarter: PropTypes.string,
    noOfCreditsPerMonth: PropTypes.string,
    ytdSales: PropTypes.arrayOf(
      PropTypes.shape({
        year: PropTypes.string,
        amount: PropTypes.string
      })
    ),
    grossProfit: PropTypes.arrayOf(
      PropTypes.shape({
        year: PropTypes.string,
        profit: PropTypes.string
      })
    ),
    netProfit: PropTypes.arrayOf(
      PropTypes.shape({
        year: PropTypes.string,
        profit: PropTypes.string
      })
    ),
    shareCapital: PropTypes.arrayOf(
      PropTypes.shape({
        year: PropTypes.string,
        amount: PropTypes.string
      })
    ),
    networth: PropTypes.arrayOf(
      PropTypes.shape({
        year: PropTypes.string,
        amount: PropTypes.string
      })
    )
  }).isRequired,

  /**
   * Function to handle form submission and continue to next step
   */
  handleContinue: PropTypes.func.isRequired,

  /**
   * Function to create a form change handler with additional functionality
   */
  createFormChangeHandler: PropTypes.func.isRequired,

  /**
   * Function to update RO prospect data in Redux store
   */
  updateRoProspect: PropTypes.func.isRequired
};
